import React, { useState, useEffect, Suspense } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { auth, provider } from './firebase';
import { 
  FiDollarSign, 
  FiUsers, 
  FiSettings, 
  FiLogOut, 
  FiShield, 
  FiArrowRight, 
  FiGrid,
  FiAlertTriangle,
  FiTrendingUp,
  FiCreditCard,
  FiCalendar,
  FiBox,
  FiClipboard,
  FiPhoneCall
} from 'react-icons/fi';
import { getFirestore, doc, getDoc, onSnapshot, collection, query, where } from 'firebase/firestore';
import AttendanceSessionMonitor from './components/AttendanceSessionMonitor';
import TaskDispatchReceiver from './components/TaskDispatchReceiver';
import BackgroundTaskToasts from './components/BackgroundTaskToasts';
import useAccountStore from './crm/store/useAccountStore';
import RandIcon from './crm/components/RandIcon';
import CRMErrorBoundary from './crm/ErrorBoundary';

// Lazy load application modules for optimized bundle splitting & security
const MainApp = React.lazy(() => import('./MainApp'));
const CRMApp = React.lazy(() => import('./crm/CRMApp'));
const SalesApp = React.lazy(() => import('./sales/SalesApp'));
const BillsManagement = React.lazy(() => import('./BillsManagement'));
const CalendarApp = React.lazy(() => import('./calendar/CalendarApp'));
const StockApp = React.lazy(() => import('./stock/StockApp'));
const CalloutsApp = React.lazy(() => import('./callouts/CalloutsApp'));
const CourtesyCallsApp = React.lazy(() => import('./courtesy/CourtesyCallsApp'));
const SettingsView = React.lazy(() => import('./crm/components/SettingsView'));
const CaseEscalationPopup = React.lazy(() => import('./crm/components/CaseEscalationPopup'));
const CaseDetailModal = React.lazy(() => import('./crm/components/Modals/CaseDetailModal'));

// Whitelist definition for CRM module access
export const TECHNICIAN_EMAIL = 'technician@abacustelecomm.co.za';

export const isTechnicianUser = (user) => {
  if (!user || typeof user.email !== 'string') return false;
  const email = user.email.trim().toLowerCase();
  return email === TECHNICIAN_EMAIL || email === 'dominic@abacustelecomm.co.za';
};

// Reusable Loading Fallback
function LoadingSpinner({ message = "Loading Secure Workspace..." }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-[#f8f9fb]">
      <div className="flex flex-col items-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        <p className="text-[#575e74] font-medium tracking-wide text-sm">{message}</p>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [activeModule, setActiveModule] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('module') === 'callouts') {
        return 'callouts';
      }
      if (params.get('module') === 'stock' || params.get('collectionId')) {
        return 'stock';
      }
      if (params.get('module') === 'calendar') {
        return 'calendar';
      }
      if (params.get('module') === 'quotes' || params.get('module') === 'quotation' || params.get('sub') === 'quotes') {
        return 'sales';
      }
      if (params.get('account') || params.get('tab') || params.get('calloutId')) {
        return 'crm';
      }
    } catch {
      // ignore
    }
    return 'selector';
  });
  const [qboStatus, setQboStatus] = useState(null);
  const [isTestUIMode, setIsTestUIMode] = useState(() => {
    try {
      const stored = localStorage.getItem('abacus_test_ui_mode');
      return stored !== null ? stored === 'true' : true;
    } catch {
      return true;
    }
  });

  const [financeInitialClientId, setFinanceInitialClientId] = useState(null);
  const [escalatedTargetCase, setEscalatedTargetCase] = useState(null);
  const [isEscalatedCaseModalOpen, setIsEscalatedCaseModalOpen] = useState(false);

  const handleSwitchToCrm = (accountId, tab = 'holdings') => {
    if (accountId) {
      try {
        useAccountStore.getState().selectAccount(accountId);
        if (tab) {
          useAccountStore.getState().setActiveTab(tab);
        }
      } catch (e) {
        console.warn('[App] Failed to set account in CRM store:', e);
      }
    }
    setActiveModule('crm');
  };

  const handleSwitchToFinance = (qboClientId, tab = 'clientele') => {
    if (qboClientId) {
      setFinanceInitialClientId(qboClientId);
    }
    setActiveModule('finance');
  };

  const handleToggleTestUIMode = () => {
    setIsTestUIMode(prev => {
      const nextVal = !prev;
      try {
        localStorage.setItem('abacus_test_ui_mode', String(nextVal));
      } catch (err) {
        console.warn('Could not save test UI mode:', err);
      }
      return nextVal;
    });
  };

  // Real-time QuickBooks API Health Listener
  useEffect(() => {
    const db = getFirestore();
    const unsubQbo = onSnapshot(doc(db, 'system_status', 'quickbooks'), (snap) => {
      if (snap.exists()) {
        setQboStatus(snap.data());
      }
    }, (err) => {
      console.warn("Could not subscribe to system_status/quickbooks:", err);
    });
    return () => unsubQbo();
  }, []);

  // Auth State Listener
    const [currentStaffMember, setCurrentStaffMember] = useState(null);

  // Subscribe to logged-in user's staff profile for shift hours
  useEffect(() => {
    if (!user?.email) {
      setCurrentStaffMember(null);
      return;
    }
    const cleanEmail = user.email.trim().toLowerCase();
    const db = getFirestore();
    const unsub = onSnapshot(collection(db, 'staff'), (snap) => {
      const match = snap.docs.find(d => (d.data().email || '').toLowerCase().trim() === cleanEmail);
      if (match) {
        setCurrentStaffMember({ id: match.id, ...match.data() });
      }
    }, (err) => {
      console.warn('Could not fetch staff profile for attendance:', err);
    });
    return () => unsub();
  }, [user]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const email = (currentUser.email || '').trim().toLowerCase();
        if (email.endsWith('@abacustelecomm.co.za')) {
          setUser(currentUser);
          
          if (email === 'technician@abacustelecomm.co.za' || email === 'dominic@abacustelecomm.co.za') {
            setUserRole('Super User');
          } else {
            try {
              const db = getFirestore();
              const roleDoc = await getDoc(doc(db, 'user_roles', email));
              if (roleDoc.exists()) {
                const fetchedRole = roleDoc.data().role;
                setUserRole(fetchedRole);
                if (fetchedRole === 'Technical') {
                  setActiveModule('crm');
                }
              } else {
                setUserRole(null);
              }
            } catch (err) {
              console.error("Failed to fetch user role:", err);
              setUserRole(null);
            }
          }

          // Deep-link routing via URL parameters (?module=stock or ?collectionId=... or ?module=crm or ?module=calendar)
          const params = new URLSearchParams(window.location.search);
          if (params.get('module') === 'courtesy_calls' || params.get('module') === 'courtesy') {
            setActiveModule('courtesy_calls');
          } else if (params.get('module') === 'callouts') {
            setActiveModule('callouts');
          } else if (params.get('module') === 'stock' || params.get('collectionId')) {
            setActiveModule('stock');
          } else if (params.get('module') === 'crm' || params.get('account')) {
            setActiveModule('crm');
          } else if (params.get('module') === 'calendar') {
            setActiveModule('calendar');
          } else if (params.get('module') === 'bills' || params.get('tab') === 'bills') {
            if (email === 'technician@abacustelecomm.co.za' || email === 'dominic@abacustelecomm.co.za') {
              setActiveModule('bills');
            } else {
              setActiveModule('finance');
            }
          } else if (params.get('module') === 'finance') {
            setActiveModule('finance');
          } else if (params.get('module') === 'quotes' || params.get('module') === 'quotation' || params.get('tab') === 'quotes' || params.get('sub') === 'quotes') {
            setActiveModule('sales');
          }
        } else {
          await signOut(auth);
          alert("Access Denied: You must use an @abacustelecomm.co.za email address to log in.");
          setUser(null);
          setUserRole(null);
          setActiveModule('selector');
        }
      } else {
        setUser(null);
        setUserRole(null);
        setActiveModule('selector');
      }
      setAuthChecking(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error("Login failed:", err);
      alert("Failed to login. Ensure Google Auth is enabled in your Firebase Console.");
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserRole(null);
      setActiveModule('selector');
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  if (authChecking) {
    return <LoadingSpinner message="Verifying session..." />;
  }

  // Unauthenticated Login Screen: Pure zero-leakage security screen
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f8f9fb] relative overflow-hidden">
        {/* Ambient subtle light glow */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-100/60 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-sky-100/60 rounded-full blur-3xl pointer-events-none"></div>

        <div className="bg-white p-10 md:p-12 text-center max-w-md w-full mx-4 z-10 border border-[#e6e8ee] shadow-xl rounded-3xl">
          <div className="w-20 h-20 bg-gradient-to-tr from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-md shadow-emerald-500/20 transform rotate-3 hover:rotate-6 transition-transform">
            <FiShield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-extrabold text-[#161a2e] mb-2 tracking-tight">
            Abacus Portal
          </h1>
          <p className="text-[#575e74] text-sm mb-8 font-normal leading-relaxed">
            Please sign in with your authorized @abacustelecomm.co.za account to access the workspace.
          </p>

          <button
            onClick={handleLogin}
            className="w-full flex items-center justify-center gap-3 bg-white text-[#161a2e] font-semibold py-3.5 px-4 rounded-xl border border-[#d3d6df] shadow-sm hover:bg-[#f8f9fb] hover:border-emerald-500 transition-all duration-200 transform hover:-translate-y-0.5 cursor-pointer text-sm"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google Workspace
          </button>
        </div>
      </div>
    );
  }

  const isSuperUser = userRole === 'Super User' || (user?.email && ['technician@abacustelecomm.co.za', 'dominic@abacustelecomm.co.za'].includes(user.email.trim().toLowerCase()));
  const isTechnicianExclusive = Boolean(user?.email && ['technician@abacustelecomm.co.za', 'dominic@abacustelecomm.co.za'].includes(user.email.trim().toLowerCase()));
  const canAccessBills = isSuperUser || isTechnicianExclusive;
  const canAccessSales = isSuperUser || userRole === 'Sales' || Boolean(user?.email && ['technician@abacustelecomm.co.za', 'dominic@abacustelecomm.co.za', 'gabbey@abacustelecomm.co.za'].includes(user.email.trim().toLowerCase()));
  const effectiveTestUIMode = isTestUIMode;
  const currentModule = activeModule;

  return (
    <div className="flex flex-col h-screen bg-[#f8f9fb] overflow-hidden font-sans text-[#161a2e]">
      {/* Top Swap Bar */}
      <header className="h-12 flex-shrink-0 bg-white border-b border-[#e6e8ee] px-4 flex items-center justify-between z-30 shadow-xs">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setActiveModule('selector')}
            className="flex items-center gap-2 px-2.5 py-1 bg-[#f1f2f6] hover:bg-[#e6e8ee] rounded-lg border border-[#d3d6df] text-[11px] font-bold text-[#161a2e] transition-colors cursor-pointer"
            title="Return to Workspace Selector"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>ABACUS</span>
            <FiGrid className="w-3 h-3 text-[#575e74] ml-0.5" />
          </button>

          <div className="tab-container !p-0.5 !rounded-lg bg-[#f1f2f6] border border-[#d3d6df] flex items-center">
            {userRole !== 'Technical' && (
              <button
                type="button"
                onClick={() => setActiveModule('finance')}
                className={`tab-btn !py-1 !px-3.5 !text-xs !rounded-md font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                  currentModule === 'finance'
                    ? 'active !bg-white !text-emerald-700 border border-[#d3d6df] shadow-xs font-bold'
                    : 'inactive text-[#575e74] hover:text-[#161a2e]'
                }`}
              >
                <RandIcon className="w-3.5 h-3.5 text-emerald-600" />
                <span>Finance</span>
              </button>
            )}
            
            <button
              type="button"
              onClick={() => setActiveModule('crm')}
              className={`tab-btn !py-1 !px-3.5 !text-xs !rounded-md font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                currentModule === 'crm'
                  ? 'active !bg-white !text-emerald-700 border border-[#d3d6df] shadow-xs font-bold'
                  : 'inactive text-[#575e74] hover:text-[#161a2e]'
              }`}
            >
              <FiUsers className="w-3.5 h-3.5" />
              <span>CRM</span>
            </button>

            {canAccessSales && (
              <button
                type="button"
                onClick={() => setActiveModule('sales')}
                className={`tab-btn !py-1 !px-3.5 !text-xs !rounded-md font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                  currentModule === 'sales'
                    ? 'active !bg-white !text-emerald-700 border border-[#d3d6df] shadow-xs font-bold'
                    : 'inactive text-[#575e74] hover:text-[#161a2e]'
                }`}
              >
                <FiTrendingUp className="w-3.5 h-3.5" />
                <span>Sales</span>
              </button>
            )}

            {canAccessBills && (
              <button
                type="button"
                onClick={() => setActiveModule('bills')}
                className={`tab-btn !py-1 !px-3.5 !text-xs !rounded-md font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                  currentModule === 'bills'
                    ? 'active !bg-white !text-amber-700 border border-[#d3d6df] shadow-xs font-bold'
                    : 'inactive text-[#575e74] hover:text-[#161a2e]'
                }`}
              >
                <FiCreditCard className="w-3.5 h-3.5" />
                <span>Bills</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => setActiveModule('calendar')}
              className={`tab-btn !py-1 !px-3.5 !text-xs !rounded-md font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                currentModule === 'calendar'
                  ? 'active !bg-white !text-blue-700 border border-[#d3d6df] shadow-xs font-bold'
                  : 'inactive text-[#575e74] hover:text-[#161a2e]'
              }`}
            >
              <FiCalendar className="w-3.5 h-3.5 text-blue-600" />
              <span>Calendar</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveModule('stock')}
              className={`tab-btn !py-1 !px-3.5 !text-xs !rounded-md font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                currentModule === 'stock'
                  ? 'active !bg-white !text-amber-800 border border-[#d3d6df] shadow-xs font-bold'
                  : 'inactive text-[#575e74] hover:text-[#161a2e]'
              }`}
            >
              <FiBox className="w-3.5 h-3.5 text-amber-600" />
              <span>Stock</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveModule('callouts')}
              className={`tab-btn !py-1 !px-3.5 !text-xs !rounded-md font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                currentModule === 'callouts'
                  ? 'active !bg-white !text-purple-700 border border-[#d3d6df] shadow-xs font-bold'
                  : 'inactive text-[#575e74] hover:text-[#161a2e]'
              }`}
            >
              <FiClipboard className="w-3.5 h-3.5 text-purple-600" />
              <span>Callouts</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveModule('courtesy_calls')}
              className={`tab-btn !py-1 !px-3.5 !text-xs !rounded-md font-semibold transition-all cursor-pointer flex items-center gap-1.5 ${
                currentModule === 'courtesy_calls'
                  ? 'active !bg-white !text-emerald-700 border border-[#d3d6df] shadow-xs font-bold'
                  : 'inactive text-[#575e74] hover:text-[#161a2e]'
              }`}
            >
              <FiPhoneCall className="w-3.5 h-3.5 text-emerald-600" />
              <span>Courtesy Calls</span>
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs">
          {isSuperUser && (
            <button
              type="button"
              onClick={() => setActiveModule(currentModule === 'settings' ? 'crm' : 'settings')}
              className={`p-1.5 rounded-lg border transition-all cursor-pointer flex items-center gap-1.5 ${
                currentModule === 'settings'
                  ? 'bg-amber-50 text-amber-800 border-amber-300 shadow-xs'
                  : 'text-[#575e74] hover:text-[#161a2e] hover:bg-[#f1f2f6] border-[#d3d6df]'
              }`}
              title="System Settings & Permissions"
            >
              <FiSettings className="w-4 h-4" />
              <span className="hidden sm:inline text-[11px] font-semibold">Settings</span>
            </button>
          )}

          <span className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full font-bold text-[10px] uppercase border ${
            userRole === 'Super User' || isSuperUser ? 'bg-amber-50 border-amber-200 text-amber-700' :
            userRole === 'Technical' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
            userRole === 'Accounts' ? 'bg-blue-50 border-blue-200 text-blue-700' :
            'bg-purple-50 border-purple-200 text-purple-700'
          }`}>
            {userRole || (isSuperUser ? 'Super User' : 'Technical')} Privileges
          </span>
          <span className="text-[#575e74] truncate max-w-[200px]" title={user.email}>
            {user.email}
          </span>
          <button
            type="button"
            onClick={handleLogout}
            className="p-1.5 text-[#7b8195] hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
            title="Sign Out"
          >
            <FiLogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* QuickBooks Token Health Alert Banner */}
      {qboStatus?.status === 'error' && (
        <div className="bg-rose-50 border-b border-rose-200 px-4 py-2 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-rose-800 z-20 shadow-xs">
          <div className="flex items-center gap-2.5">
            <FiAlertTriangle className="w-4 h-4 text-rose-600 shrink-0 animate-bounce" />
            <span>
              <strong className="text-rose-900 font-bold">QuickBooks API Alert:</strong> {qboStatus.message || "Your QuickBooks OAuth refresh token has expired or is invalid. Automatic invoicing and sync are paused."}
            </span>
          </div>
          {isSuperUser && (
            <button
              type="button"
              onClick={() => setActiveModule('settings')}
              className="px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 shrink-0 shadow-xs text-[11px]"
            >
              <span>Manage QuickBooks Token</span>
              <FiArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Main Content Workspace Container */}
            {/* Attendance Session Clock-in, Midnight Invalidation & Shift Logoff Monitor */}
      <AttendanceSessionMonitor 
        user={user} 
        staffMember={currentStaffMember} 
        onForcedLogout={(reason) => {
          handleLogout();
          if (reason) alert(reason);
        }} 
      />

      {/* Real-time Boss Priority Task Dispatch Receiver */}
      <TaskDispatchReceiver 
        currentUser={user}
        onNavigateToAccount={(accountId) => {
          useAccountStore.getState().selectAccount(accountId);
          setActiveModule('crm');
        }}
      />

      {/* Global Background Task Progress & Failure Notifier */}
      <BackgroundTaskToasts />

      <div className="flex-1 min-h-0 relative">
        <Suspense fallback={<LoadingSpinner message={
          currentModule === 'settings' ? "Loading System Settings..." :
          currentModule === 'crm' ? "Loading CRM Workspace..." :
          currentModule === 'finance' ? "Loading Finance Dashboard..." :
          currentModule === 'bills' ? "Loading Bills & Suppliers..." :
          "Loading Workspace..."
        } />}>
          {/* Workspace Selector */}
          <div className={`h-full w-full ${currentModule === 'selector' ? 'flex flex-col' : 'hidden'}`}>
            <div className="h-full w-full flex flex-col items-center justify-center p-6 md:p-12 overflow-y-auto bg-[#f8f9fb]">
              <div className="max-w-4xl w-full text-center space-y-3 mb-10 animate-in fade-in duration-300">
                <h2 className="text-3xl md:text-4xl font-black text-[#161a2e] tracking-tight">
                  Select Workspace
                </h2>
                <p className="text-[#575e74] text-sm md:text-base max-w-lg mx-auto">
                  Choose a workspace to launch, or toggle between them anytime using the top bar.
                </p>
              </div>

              <div className={`grid grid-cols-1 ${canAccessBills ? 'md:grid-cols-2 lg:grid-cols-4' : (canAccessSales ? 'md:grid-cols-3' : 'md:grid-cols-2')} gap-6 max-w-6xl w-full animate-in fade-in duration-500`}>
                {/* FINANCE PORTAL CARD */}
                {userRole !== 'Technical' && (
                  <div 
                    onClick={() => setActiveModule('finance')}
                    className="group relative bg-white hover:border-emerald-500/80 border border-[#e6e8ee] rounded-3xl p-8 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-lg hover:-translate-y-1 flex flex-col justify-between"
                  >
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 group-hover:scale-110 group-hover:bg-emerald-100 transition-all duration-300">
                          <RandIcon className="w-7 h-7" />
                        </div>
                        <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">
                          Finance
                        </span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#161a2e] group-hover:text-emerald-700 transition-colors">
                          Finance & Invoicing
                        </h3>
                      </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-[#f1f2f6] flex items-center justify-between text-emerald-700 font-bold text-sm">
                      <span>Open Finance Workspace</span>
                      <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                )}

                {/* CRM PORTAL CARD */}
                <div 
                  onClick={() => setActiveModule('crm')}
                  className="group relative bg-white hover:border-cyan-500/80 border border-[#e6e8ee] rounded-3xl p-8 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-lg hover:-translate-y-1 flex flex-col justify-between"
                >
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="w-14 h-14 rounded-2xl bg-cyan-50 border border-cyan-200 flex items-center justify-center text-cyan-600 group-hover:scale-110 group-hover:bg-cyan-100 transition-all duration-300">
                        <FiUsers className="w-7 h-7" />
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200 font-mono">
                        CRM
                      </span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-[#161a2e] group-hover:text-cyan-700 transition-colors">
                        Custom CRM
                      </h3>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-[#f1f2f6] flex items-center justify-between text-cyan-700 font-bold text-sm">
                    <span>Open CRM Workspace</span>
                    <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>

                {/* SALES & PROVISIONING PORTAL CARD */}
                {canAccessSales && (
                  <div 
                    onClick={() => setActiveModule('sales')}
                    className="group relative bg-white hover:border-emerald-500/80 border border-[#e6e8ee] rounded-3xl p-8 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-lg hover:-translate-y-1 flex flex-col justify-between"
                  >
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 group-hover:scale-110 group-hover:bg-emerald-100 transition-all duration-300">
                          <FiTrendingUp className="w-7 h-7" />
                        </div>
                        <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">
                          Sales & Provisioning
                        </span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#161a2e] group-hover:text-emerald-700 transition-colors">
                          Sales Pipeline
                        </h3>
                      </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-[#f1f2f6] flex items-center justify-between text-emerald-700 font-bold text-sm">
                      <span>Open Sales Workspace</span>
                      <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                )}

                {/* BILLS & SUPPLIERS EXCLUSIVE PORTAL CARD */}
                {canAccessBills && (
                  <div 
                    onClick={() => setActiveModule('bills')}
                    className="group relative bg-white hover:border-amber-500/80 border border-[#e6e8ee] rounded-3xl p-8 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-lg hover:-translate-y-1 flex flex-col justify-between"
                  >
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 group-hover:scale-110 group-hover:bg-amber-100 transition-all duration-300">
                          <FiCreditCard className="w-7 h-7" />
                        </div>
                        <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-mono">
                          Accounts AP
                        </span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#161a2e] group-hover:text-amber-700 transition-colors">
                          Bills & Suppliers
                        </h3>
                      </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-[#f1f2f6] flex items-center justify-between text-amber-700 font-bold text-sm">
                      <span>Open Bills Workspace</span>
                      <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                )}

                {/* CALENDAR & FIELD SERVICE CARD */}
                <div 
                  onClick={() => setActiveModule('calendar')}
                  className="group relative bg-white hover:border-blue-500/80 border border-[#e6e8ee] rounded-3xl p-8 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-lg hover:-translate-y-1 flex flex-col justify-between"
                >
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 group-hover:scale-110 group-hover:bg-blue-100 transition-all duration-300">
                        <FiCalendar className="w-7 h-7" />
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-mono">
                        Calendar
                      </span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-[#161a2e] group-hover:text-blue-700 transition-colors">
                        Calendar
                      </h3>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-[#f1f2f6] flex items-center justify-between text-blue-700 font-bold text-sm">
                    <span>Open Calendar Workspace</span>
                    <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>

                {/* STOCK & COLLECTIONS CARD */}
                <div 
                  onClick={() => setActiveModule('stock')}
                  className="group relative bg-white hover:border-amber-500/80 border border-[#e6e8ee] rounded-3xl p-8 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-lg hover:-translate-y-1 flex flex-col justify-between"
                >
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 group-hover:scale-110 group-hover:bg-amber-100 transition-all duration-300">
                        <FiBox className="w-7 h-7" />
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-mono">
                        Stock & Logistics
                      </span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-[#161a2e] group-hover:text-amber-700 transition-colors">
                        Stock & Inventory
                      </h3>
                      <p className="text-xs text-[#575e74] mt-1">
                        Supplier collections, stock levels & movement ledger
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-[#f1f2f6] flex items-center justify-between text-amber-700 font-bold text-sm">
                    <span>Open Stock Workspace</span>
                    <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>

                {/* CALLOUTS JOB CARDS & INVOICING CARD */}
                <div 
                  onClick={() => setActiveModule('callouts')}
                  className="group relative bg-white hover:border-purple-500/80 border border-[#e6e8ee] rounded-3xl p-8 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-lg hover:-translate-y-1 flex flex-col justify-between"
                >
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="w-14 h-14 rounded-2xl bg-purple-50 border border-purple-200 flex items-center justify-center text-purple-600 group-hover:scale-110 group-hover:bg-purple-100 transition-all duration-300">
                        <FiClipboard className="w-7 h-7" />
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-mono">
                        Job Cards & Billing
                      </span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-[#161a2e] group-hover:text-purple-700 transition-colors">
                        Callouts
                      </h3>
                      <p className="text-xs text-[#575e74] mt-1">
                        Completed & scheduled job cards, stock verification & push to client holdings
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-[#f1f2f6] flex items-center justify-between text-purple-700 font-bold text-sm">
                    <span>Open Callouts Workspace</span>
                    <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>

                {/* BUSINESS COURTESY CALLS CARD */}
                <div 
                  onClick={() => setActiveModule('courtesy_calls')}
                  className="group relative bg-white hover:border-emerald-500/80 border border-[#e6e8ee] rounded-3xl p-8 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-lg hover:-translate-y-1 flex flex-col justify-between"
                >
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 group-hover:scale-110 group-hover:bg-emerald-100 transition-all duration-300">
                        <FiPhoneCall className="w-7 h-7" />
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono">
                        Calls
                      </span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-[#161a2e] group-hover:text-emerald-700 transition-colors">
                        Courtesy Calls
                      </h3>
                      <p className="text-xs text-[#575e74] mt-1">
                        Regular check-ins & customer success rotation for all business clients
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-[#f1f2f6] flex items-center justify-between text-emerald-700 font-bold text-sm">
                    <span>Open Courtesy Calls Queue</span>
                    <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* System Settings (Super User) */}
          {isSuperUser && (
            <div className={`h-full w-full overflow-y-auto p-4 md:p-6 bg-[#f8f9fb] ${currentModule === 'settings' ? 'block' : 'hidden'}`}>
              <SettingsView isTestUIMode={effectiveTestUIMode} user={user} />
            </div>
          )}

          {/* Sales Workspace */}
          {canAccessSales && (
            <div className={`h-full w-full ${currentModule === 'sales' ? 'flex flex-col' : 'hidden'}`}>
              <CRMErrorBoundary onSwitchToFinance={handleSwitchToFinance}>
                <SalesApp 
                user={user} 
                userRole={userRole || (isSuperUser ? 'Super User' : 'Technical')} 
                isSuperUser={isSuperUser}
                handleLogout={handleLogout} 
                onSwitchToCrm={handleSwitchToCrm}
                onSwitchToFinance={handleSwitchToFinance}
              onBack={() => setActiveModule('selector')}
              />
              </CRMErrorBoundary>
            </div>
          )}

          {/* CRM Workspace */}
          <div className={`h-full w-full ${currentModule === 'crm' ? 'flex flex-col' : 'hidden'}`}>
            <CRMApp 
              user={user} 
              userRole={userRole || (isSuperUser ? 'Super User' : 'Technical')} 
              handleLogout={handleLogout} 
              onSwitchToFinance={handleSwitchToFinance}
              onBack={() => setActiveModule('selector')} 
              isTestUIMode={effectiveTestUIMode}
            />
          </div>

          {/* Finance Workspace */}
          {userRole !== 'Technical' && (
            <div className={`h-full w-full ${userRole === 'Sales' ? 'finance-readonly' : ''} ${currentModule === 'finance' ? 'flex flex-col' : 'hidden'}`}>
              <MainApp 
                user={user} 
                userRole={userRole || (isSuperUser ? 'Super User' : 'Technical')} 
                handleLogout={handleLogout} 
                onSwitchToCrm={handleSwitchToCrm}
                initialSelectedClientId={financeInitialClientId}
                isTestUIMode={effectiveTestUIMode}
              />
            </div>
          )}

          {/* Bills Workspace */}
          {canAccessBills && (
            <div className={`h-full w-full ${currentModule === 'bills' ? 'flex flex-col' : 'hidden'}`}>
              <BillsManagement 
                user={user} 
                userRole={userRole || 'Super User'} 
                isSuperUser={true}
              />
            </div>
          )}

          {/* Calendar Workspace */}
          <div className={`h-full w-full ${currentModule === 'calendar' ? 'flex flex-col' : 'hidden'}`}>
            <CalendarApp 
              user={user} 
              isSuperUser={isSuperUser} 
              onSwitchToCrm={handleSwitchToCrm} 
              isTestUIMode={effectiveTestUIMode} 
            />
          </div>

          {/* Stock Workspace */}
          <div className={`h-full w-full ${currentModule === 'stock' ? 'flex flex-col' : 'hidden'}`}>
            <StockApp 
              user={user} 
              userRole={userRole || (isSuperUser ? 'Super User' : 'Technical')} 
              isSuperUser={isSuperUser} 
            />
          </div>

          {/* Courtesy Calls Workspace */}
          <div className={`h-full w-full overflow-y-auto ${currentModule === 'courtesy_calls' ? 'flex flex-col' : 'hidden'}`}>
            <CourtesyCallsApp 
              onBack={() => setActiveModule('selector')}
              onOpenCrmAccount={(acc) => handleSwitchToCrm(acc.id, 'courtesy_calls')}
            />
          </div>

          {/* Callouts Job Cards & Billing Workspace */}
          <div className={`h-full w-full ${currentModule === 'callouts' ? 'flex flex-col' : 'hidden'}`}>
            <CalloutsApp 
              user={user} 
              userRole={userRole || (isSuperUser ? 'Super User' : 'Technical')} 
              isSuperUser={isSuperUser} 
              onSwitchToCrm={handleSwitchToCrm}
              onSwitchToFinance={handleSwitchToFinance}
              onBack={() => setActiveModule('selector')}
            />
          </div>
        </Suspense>

        {/* Global Super User Escalation Alert Popup (Persistent until checked or dismissed) */}
        <Suspense fallback={null}>
          {isSuperUser && (
            <CaseEscalationPopup
              currentUser={user}
              isSuperUser={isSuperUser}
              onOpenCase={(caseItem) => {
                setEscalatedTargetCase(caseItem);
                setIsEscalatedCaseModalOpen(true);
              }}
            />
          )}

          {/* Global Case Detail Modal opened from Escalation Popup */}
          {isEscalatedCaseModalOpen && escalatedTargetCase && (
            <CaseDetailModal
              isOpen={isEscalatedCaseModalOpen}
              onClose={() => {
                setIsEscalatedCaseModalOpen(false);
                setEscalatedTargetCase(null);
              }}
              caseItem={escalatedTargetCase}
              currentUser={user}
            />
          )}
        </Suspense>
      </div>
    </div>
  );
}